/**
 * @pit/math — pure integer math for the deterministic sim.
 *
 * Zero dependencies. Governed by sim-purity ESLint scope
 * (`packages/math/src/**`): only `imath.ts` may use raw `/`, `%`,
 * `Math.floor`, `Math.sqrt`. Everything else in this package composes those
 * primitives.
 *
 * See wp1-protocol.md Part A for the full contract.
 * See imath.ts for the overflow policy (no BigInt, no fixed-point library).
 */

import { SimMathError, assertInt } from "./imath.js";

export { SimMathError, idiv, imod, isqrt, assertInt } from "./imath.js";

/**
 * Clamp `x` to the closed integer range `[lo, hi]`. Requires all three args
 * to be integers with `lo ≤ hi`; throws SimMathError otherwise.
 */
export function clampInt(x: number, lo: number, hi: number): number {
  assertInt(x, "clampInt.x");
  assertInt(lo, "clampInt.lo");
  assertInt(hi, "clampInt.hi");
  if (lo > hi) {
    throw new SimMathError("clampInt.range", { lo, hi }, "lo > hi");
  }
  if (x < lo) return lo;
  if (x > hi) return hi;
  return x;
}

/**
 * Squared Euclidean distance between two integer points, integer-only.
 *
 * The whole sim uses squared-distance comparisons wherever possible — only
 * `isqrt` bridges back to a linear scalar (movement stepping in WP3). All
 * four coords must be integers.
 *
 * With sim coords ≤ 4096, `distSq ≤ 2 * (2*4096)² ≈ 1.3e8` — comfortably
 * inside `MAX_SAFE_INTEGER`. `assertInt` on the result guards regardless.
 */
export function distSq(ax: number, ay: number, bx: number, by: number): number {
  assertInt(ax, "distSq.ax");
  assertInt(ay, "distSq.ay");
  assertInt(bx, "distSq.bx");
  assertInt(by, "distSq.by");
  const dx = bx - ax;
  const dy = by - ay;
  const result = dx * dx + dy * dy;
  assertInt(result, "distSq.result");
  return result;
}
